import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import ActionSidePanel from 'src/components/design-system/ActionSidePanel';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import useConfirm from 'src/hooks/useConfirm';
import ShoppingListForm from './ShoppingListForm';
import { useShoppingListForm } from './useShoppingListForm';

export interface CreateShoppingListSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess?: () => void | Promise<void>;
}

const CreateShoppingListSidePanel: FC<CreateShoppingListSidePanelProps> = ({
  isOpen,
  onClose,
  onSaveSuccess,
}) => {
  const { t } = useTranslation();
  const { confirm } = useConfirm();

  const {
    values,
    errors,
    touched,
    isDirty,
    isValid,
    isSaving,
    setFieldValue,
    handleFieldBlur,
    handleSave,
  } = useShoppingListForm({
    mode: 'create',
    isOpen,
    onSuccess: onSaveSuccess ?? onClose,
  });

  const confirmDiscardChanges = async (): Promise<boolean> => {
    if (!isDirty) {
      return true;
    }

    return confirm({
      title: t('Unsaved Changes'),
      content: t('You have unsaved changes. Do you want to discard them?'),
      cancelButtonText: t('Cancel'),
      confirmButtonText: t('Yes, Discard'),
      width: '500px',
    });
  };

  const handleClose = async () => {
    const shouldClose = await confirmDiscardChanges();
    if (shouldClose) {
      onClose();
    }
  };

  const saveDisabledTooltip = (
    <PopUpContentWrapper type="Information" contents={[t('No changes made')]} />
  );

  return (
    <ActionSidePanel
      isOpen={isOpen}
      onClose={handleClose}
      title={t('Create New List')}
      showTitleTooltip={false}
      onSave={handleSave}
      onCancel={handleClose}
      saveDisabled={!isValid || !isDirty}
      saveLoading={isSaving}
      cancelDisabled={isSaving}
      saveDisabledTooltip={saveDisabledTooltip}
      width="480px"
    >
      <ShoppingListForm
        values={values}
        errors={errors}
        touched={touched}
        onFieldChange={setFieldValue}
        onFieldBlur={handleFieldBlur}
      />
    </ActionSidePanel>
  );
};

export default CreateShoppingListSidePanel;
